/**
 * Team leave — V10 §6.
 *
 * The founder approves leave and then loses sight of it: the approval queue
 * empties on decision, and a decided request goes nowhere anybody can look. The
 * data has always been there; the page never asked for it.
 *
 * Three questions, in the order somebody actually asks them:
 *
 *  1. **Who is out right now?** — the question that interrupts a day.
 *  2. **Who is out next?** — the one that plans a week.
 *  3. **What did we decide, and who decided it?** — the one that settles an
 *     argument, which is why it carries the decider and the decision date and
 *     is filterable by person and month.
 *
 * Reasons are shown here and only here. `leaveCalendar` deliberately carries
 * names, dates and types and never reasons (§3.4) because it is visible to
 * everyone; this tab is behind `leave.approve`, and an approver decided on the
 * reason in the first place.
 */

import { useMemo, useState } from 'react';
import { CalendarClock, CalendarDays, History, Users } from 'lucide-react';
import type { LeaveStatus, LeaveType } from '@virtuetasks/types';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { UserLink } from '@/components/shared/UserLink';
import { useUsers } from '@/features/settings/api';
import { useLeaves, type LeaveView } from './api';

const TYPE_LABEL: Record<LeaveType, string> = {
  leave: 'Leave',
  half_day: 'Half day',
  wfh: 'Work from home',
};

/** Decided, in the sense §6 means: somebody acted on it. */
const DECIDED: LeaveStatus[] = ['approved', 'rejected', 'revoked'];

function statusTone(status: string): 'accent' | 'warn' | 'danger' | 'neutral' {
  if (status === 'approved') return 'accent';
  if (status === 'pending') return 'warn';
  if (status === 'rejected' || status === 'revoked') return 'danger';
  return 'neutral';
}

/** `YYYY-MM-DD` for an instant, with no timezone reinterpretation. */
function isoDay(at: Date): string {
  return new Date(at.getTime() - at.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function dayOf(iso: string): string {
  return iso.slice(0, 10);
}

function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function dateRange(leave: LeaveView): string {
  const start = longDate(leave.startDate);
  const end = longDate(leave.endDate);
  return start === end ? start : `${start} → ${end}`;
}

/** The first working-relevant fact about somebody who is out: when they are back. */
function returnsOn(leave: LeaveView): string {
  const end = new Date(leave.endDate);
  end.setDate(end.getDate() + 1);
  return longDate(end.toISOString());
}

function PersonLine({ leave }: { leave: LeaveView }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <UserLink
        name={leave.userName}
        userId={leave.userId}
        avatarUrl={leave.userAvatarUrl ?? undefined}
        avatarOnly
        size={24}
      />
      <span className="truncate text-[13px] font-medium">
        <UserLink userId={leave.userId} name={leave.userName} nameOnly />
      </span>
    </span>
  );
}

function Section({
  title,
  icon,
  count,
  children,
  testId,
}: {
  title: string;
  icon: React.ReactNode;
  count?: number;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <section data-testid={testId}>
      <h2 className="mb-2 flex items-center gap-2 text-[13px] font-medium">
        {icon}
        {title}
        {count !== undefined && count > 0 && <Badge tone="neutral">{count}</Badge>}
      </h2>
      {children}
    </section>
  );
}

export function TeamLeave() {
  const today = useMemo(() => isoDay(new Date()), []);

  /**
   * One request for both live sections.
   *
   * "Ongoing or future approved leave" is a single query — `endDate >= today`
   * — and splitting it in the client is exact, where two overlapping server
   * queries would double-count a leave that started yesterday and runs into
   * next week.
   */
  const { data: current = [], isLoading: currentLoading } = useLeaves({
    status: 'approved',
    from: today,
    sort: 'soonest',
  });

  const onLeaveToday = current.filter((leave) => dayOf(leave.startDate) <= today);
  const upcoming = current.filter((leave) => dayOf(leave.startDate) > today);

  return (
    <div className="flex flex-col gap-5" data-testid="team-leave">
      <Section
        title="On leave today"
        icon={<Users className="size-3.5" />}
        count={onLeaveToday.length}
        testId="on-leave-today"
      >
        {currentLoading ? (
          <Skeleton className="h-20" />
        ) : onLeaveToday.length === 0 ? (
          <p className="vt-surface px-3 py-4 text-center text-[13px] vt-muted">
            Everybody is in today.
          </p>
        ) : (
          <ul className="vt-surface divide-y divide-[var(--border)]">
            {onLeaveToday.map((leave) => (
              <li
                key={leave._id}
                className="flex items-center gap-2 px-3 py-2.5"
                data-testid="on-leave-row"
                data-user-id={leave.userId}
              >
                <PersonLine leave={leave} />
                <Badge tone="accent">{TYPE_LABEL[leave.type]}</Badge>
                <span className="ml-auto shrink-0 text-[12px] vt-muted" data-testid="returns-on">
                  Back {returnsOn(leave)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="Upcoming"
        icon={<CalendarClock className="size-3.5" />}
        count={upcoming.length}
        testId="upcoming-leave"
      >
        {currentLoading ? (
          <Skeleton className="h-20" />
        ) : upcoming.length === 0 ? (
          <p className="vt-surface px-3 py-4 text-center text-[13px] vt-muted">
            No approved leave is coming up.
          </p>
        ) : (
          <ul className="vt-surface divide-y divide-[var(--border)]">
            {upcoming.map((leave) => (
              <li
                key={leave._id}
                className="flex items-center gap-2 px-3 py-2.5"
                data-testid="upcoming-leave-row"
                data-user-id={leave.userId}
                data-start={dayOf(leave.startDate)}
              >
                <PersonLine leave={leave} />
                <Badge tone="accent">{TYPE_LABEL[leave.type]}</Badge>
                <span className="ml-auto shrink-0 text-[12px] vt-muted">
                  {dateRange(leave)} · {leave.workingDays} working{' '}
                  {leave.workingDays === 1 ? 'day' : 'days'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <LeaveHistory />
    </div>
  );
}

function LeaveHistory() {
  const { data: people = [] } = useUsers();
  const [userId, setUserId] = useState('');
  const [month, setMonth] = useState('');

  const range = useMemo(() => {
    if (!month) return {};
    const [year, monthNumber] = month.split('-').map(Number);
    const from = `${month}-01`;
    // The last day of the month, without touching a local Date constructor.
    const to = new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10);
    return { from, to };
  }, [month]);

  const { data: history = [], isLoading } = useLeaves({
    statuses: DECIDED,
    userId: userId || undefined,
    ...range,
    limit: 200,
  });

  return (
    <Section title="History" icon={<History className="size-3.5" />} testId="leave-history">
      <div className="mb-2 flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="history-member">Member</Label>
          <select
            id="history-member"
            data-testid="history-member"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
            className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
          >
            <option value="">Everyone</option>
            {people
              .filter((person) => person.status === 'active')
              .map((person) => (
                <option key={person._id} value={person._id}>
                  {person.name}
                </option>
              ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="history-month">Month</Label>
          <Input
            id="history-month"
            type="month"
            data-testid="history-month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            className="h-8 w-[150px]"
          />
        </div>

        {(userId || month) && (
          <button
            type="button"
            data-testid="history-clear"
            onClick={() => {
              setUserId('');
              setMonth('');
            }}
            className="h-8 text-[12px] vt-muted hover:text-[var(--text)]"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-24" />
      ) : history.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="size-6" />}
          title="Nothing decided yet"
          description="Approved, rejected and revoked requests appear here with who decided them and when."
        />
      ) : (
        <div className="vt-surface overflow-x-auto">
          <table className="min-w-full border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-left">
                <th scope="col" className="px-3 py-2 font-medium">
                  Requester
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Dates
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Type
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Decision
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Decided by
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  Decided
                </th>
              </tr>
            </thead>
            <tbody>
              {history.map((leave) => (
                <tr
                  key={leave._id}
                  className="border-b border-[var(--border)] last:border-0"
                  data-testid="leave-history-row"
                  data-user-id={leave.userId}
                  data-status={leave.status}
                >
                  <th scope="row" className="px-3 py-2 text-left font-normal">
                    <UserLink userId={leave.userId} name={leave.userName} nameOnly />
                  </th>
                  <td className="px-3 py-2 vt-muted">{dateRange(leave)}</td>
                  <td className="px-3 py-2">{TYPE_LABEL[leave.type]}</td>
                  <td className="px-3 py-2">
                    <Badge tone={statusTone(leave.status)}>{leave.status}</Badge>
                  </td>
                  <td className="px-3 py-2" data-testid="history-decider">
                    {leave.deciderName ?? '—'}
                  </td>
                  <td className="px-3 py-2 vt-muted" data-testid="history-decided-at">
                    {leave.decidedAt ? longDate(leave.decidedAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
